import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, Spinner, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { Toaster } from 'react-hot-toast';
import Routes from './routes/Routes';
import { AuthService } from './services/authService';

/**
 * Main Application Component
 */
function App() {
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    
    // Application state management with proper initialization
    const [isLoading, setIsLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [appConfig, setAppConfig] = useState(null);
    const [authServiceReady, setAuthServiceReady] = useState(false);
    const [userStatus, setUserStatus] = useState(null);

    // Refs to prevent unnecessary re-renders and track initialization
    const initializationAttempted = useRef(false);
    const authServiceInitialized = useRef(false);
    const errorHandled = useRef(false); // Prevent error loops

    // Chakra UI theme-aware styling
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const textColor = useColorModeValue('gray.600', 'gray.300');

    /**
     * Clear stored session data
     * Removes all authentication-related data from storage
     * Prevents storage pollution and ensures clean state
     */
    const clearStoredSession = useCallback(() => {
        try {
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('loginTime');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('userProfileImage');
        } catch (error) {
            console.error('Failed to clear session storage:', error);
        }
    }, []);

    /**
     * Initialize AuthService with MSAL instance
     */
    const initializeAuthService = useCallback(async () => {
        // Prevent multiple initializations
        if (!instance || authServiceInitialized.current) {
            return;
        }

        try {
            // Initialize AuthService with MSAL instance for login operations
            AuthService.initializeMsal(instance);
            authServiceInitialized.current = true;
            setAuthServiceReady(true);

        } catch (error) {
            console.error('AuthService initialization failed:', error);
            setAuthError(`AuthService initialization failed: ${error.message}`);
        }
    }, [instance]);

    /**
     * Restore user session from localStorage
     * Attempts to restore user session from stored credentials
     * Validates session integrity and MSAL account presence
     */
    const restoreUserSession = useCallback(() => {
        try {
            const userEmail = localStorage.getItem('userEmail');
            const userName = localStorage.getItem('userName');
            const userRole = localStorage.getItem('userRole');
            const loginTime = localStorage.getItem('loginTime');
            const sessionId = localStorage.getItem('sessionId');

            // Check if we have valid stored session data
            if (!userEmail || !userRole || !sessionId) {
                return null;
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(userEmail)) {
                clearStoredSession();
                return null;
            }

            const restoredProfile = {
                id: sessionId,
                email: userEmail,
                name: userName || userEmail.split('@')[0] || 'Admin User',
                role: userRole,
                profileImage: localStorage.getItem('userProfileImage') || null,
                lastLogin: loginTime || new Date().toISOString(),
                status: 'approved' // Assume approved if session exists
            };

            return restoredProfile;

        } catch (error) {
            console.error('Failed to restore user session:', error);
            clearStoredSession();
            return null;
        }
    }, [clearStoredSession]);

    /**
     * Store user session securely
     * Saves user session data to localStorage with validation
     * Prevents duplicate or unnecessary data storage
     */
    const storeUserSession = useCallback((userInfo) => {
        try {
            // Only store if data has changed or doesn't exist
            const currentEmail = localStorage.getItem('userEmail');
            const currentSessionId = localStorage.getItem('sessionId');
            
            if (currentEmail === userInfo.email && currentSessionId) {
                return false; // No storage operation performed
            }

            // Clear any existing session first
            clearStoredSession();

            // Store new session data
            localStorage.setItem('userEmail', userInfo.email);
            localStorage.setItem('userRole', userInfo.role);
            localStorage.setItem('userName', userInfo.name);
            localStorage.setItem('loginTime', userInfo.lastLogin);
            localStorage.setItem('sessionId', userInfo.id);

            return true; // Storage operation performed

        } catch (storageError) {
            console.warn('Failed to store user session:', storageError);
        }
    }, [clearStoredSession]);

    /**
     * Handle authenticated user initialization with account
     * Processes MSAL account data and creates user profile
     */
    const handleAuthenticatedUserWithAccount = useCallback(async (accountData) => {
        try {
            if (!accountData || !accountData.username) {
                throw new Error('Invalid account information received from authentication provider');
            }

            const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const loginTime = new Date().toISOString();

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(accountData.username)) {
                throw new Error('Invalid email format received from authentication provider');
            }

            // Complete AuthService initialization with user context
            try {
                const userProfile = await AuthService.initializeUserSession(accountData, null);
                
                // Create proper session data from the real user profile returned by AuthService
                const userInfo = {
                    id: sessionId,
                    email: userProfile.email,
                    name: userProfile.name || userProfile.displayName || accountData.name,
                    role: userProfile.role,
                    lastLogin: loginTime,
                    status: userProfile.status  // This will be the real status from database
                };

                // Store user session
                storeUserSession(userInfo);
                setUserProfile(userInfo);
                setUserStatus('approved'); 
                
            } catch (authServiceError) {
                console.error('Authentication service user session initialization failed:', authServiceError);
                
                // Handle specific auth service errors without throwing
                const errorType = AuthService.handleAuthError(authServiceError);
                
                switch (errorType) {
                    case 'PENDING_APPROVAL':
                        setUserStatus('pending');
                        setAuthError('Your account is pending approval. Please wait for administrator approval.');
                        break;
                    case 'ACCOUNT_REJECTED':
                        setUserStatus('rejected');
                        setAuthError('Your account has been rejected. Please contact administrator.');
                        break;
                    case 'ACCOUNT_SUSPENDED':
                        setUserStatus('suspended');
                        setAuthError('Your account has been suspended. Please contact administrator.');
                        break;
                    case 'USER_CREATION_FAILED':
                        setUserStatus('error');
                        setAuthError('Failed to create user account. Please contact administrator.');
                        break;
                    default:
                        setUserStatus('error');
                        setAuthError('Authentication failed. Please try again.');
                }
                
                // Don't throw error here to prevent infinite loops
                return;
            }

        } catch (error) {
            console.error('User session initialization failed:', error);
            setAuthError(`Failed to initialize user session: ${error.message}`);
            setUserStatus('error');
        }
    }, [storeUserSession]);

    /**
     * Handle unauthenticated user state
     * Clears session data and resets user profile
     * Maintains clean state for unauthenticated users
     */
    const handleUnauthenticatedUser = useCallback(() => {
        try {
            clearStoredSession();
            
            try {
                sessionStorage.clear();
            } catch (error) {
                console.warn('Failed to clear sessionStorage:', error);
            }

            setUserProfile(null);
            setUserStatus(null);
            AuthService.clearUserSession();

        } catch (error) {
            console.error('Error during session cleanup:', error);
            setUserProfile(null);
            setUserStatus(null);
        }
    }, [clearStoredSession]);

    /**
     * Application initialization handler
     * Handles both fresh authentication and session restoration scenarios
     * Implements proper validation and error recovery
     * Prevents infinite loops with better error handling
     */
    const initializeApplication = useCallback(async () => {
        if (initializationAttempted.current) {
            return;
        }

        try {
            initializationAttempted.current = true;
            setIsLoading(true);
            setAuthError(null);
            setUserStatus(null);
            errorHandled.current = false;

            // Load application configuration first (always needed)
            const config = window.ADI_CONFIG || {
                app: {
                    name: 'Prism Monitor',
                    version: '1.0.0',
                    features: {
                        notifications: {
                            autoHideDuration: 5000
                        }
                    }
                }
            };
            setAppConfig(config);

            // Check if user just logged out, preventing auto re-login
            const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
            if (isLoggingOut) {
                localStorage.removeItem('isLoggingOut');
                clearStoredSession();
                
                // Still need to initialize AuthService for next login
                await initializeAuthService();
                
                handleUnauthenticatedUser();
                setIsLoading(false);
                return;
            }

            // Ensure AuthService is initialized first
            await initializeAuthService();

            if (isAuthenticated) {
                // User is authenticated by MSAL
                const accounts = instance.getAllAccounts();
                
                if (accounts.length > 0) {
                    // Use the first account (or active account)
                    const activeAccount = instance.getActiveAccount() || accounts[0];
                    
                    // Initialize user session with the account from MSAL
                    await handleAuthenticatedUserWithAccount(activeAccount);
                } else {
                    // Try to restore from localStorage if MSAL account is missing
                    const restoredProfile = restoreUserSession();
                    if (restoredProfile) {
                        setUserProfile(restoredProfile);
                        setUserStatus('approved');
                    } else {
                        handleUnauthenticatedUser();
                    }
                }
            } else {
                // Not authenticated by MSAL - check for valid stored session
                const restoredProfile = restoreUserSession();
                if (restoredProfile) {
                    setUserProfile(restoredProfile);
                    setUserStatus('approved');
                    
                    // Try to find the account in MSAL
                    const accounts = instance.getAllAccounts();
                    const matchingAccount = accounts.find(acc => acc.username === restoredProfile.email);
                    
                    if (matchingAccount) {
                        instance.setActiveAccount(matchingAccount);
                        
                        // Re-initialize AuthService with the found account
                        try {
                            AuthService.initializeUserSession(matchingAccount, restoredProfile);
                        } catch (authServiceError) {
                            console.warn('AuthService re-initialization failed:', authServiceError);
                        }
                    } else {
                        // The stored session is invalid if no MSAL account exists
                        handleUnauthenticatedUser();
                    }
                } else {
                    handleUnauthenticatedUser();
                }
            }

        } catch (error) {
            console.error('Application initialization failed:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            });
            
            if (!errorHandled.current) {
                const errorMessage = process.env.NODE_ENV === 'production' 
                    ? 'Application initialization failed. Please refresh the page.'
                    : error.message || 'Application initialization failed';
                
                setAuthError(errorMessage);
                setUserStatus('error');
                errorHandled.current = true;
            }
            
            initializationAttempted.current = false; // Allow retry on error
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, initializeAuthService, handleAuthenticatedUserWithAccount, handleUnauthenticatedUser, instance, restoreUserSession, clearStoredSession]);

    /**
     * Handle application-level errors
     * Provides consistent error handling and user feedback
     * Prevents error loops and infinite redirects
     */
    const handleApplicationError = useCallback((error) => {
        if (errorHandled.current) {
            return; // Prevent error loops
        }

        console.error('Application error occurred:', {
            error: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
        });

        const isAuthError = error.message?.includes('authentication') || 
                           error.message?.includes('token') || 
                           error.message?.includes('unauthorized');
        
        const isNetworkError = error.message?.includes('network') || 
                              error.message?.includes('fetch') || 
                              error.message?.includes('connection');

        let userMessage = 'An unexpected error occurred';
        
        if (isAuthError) {
            userMessage = 'Authentication error. Please sign in again.';
            handleUnauthenticatedUser();
        } else if (isNetworkError) {
            userMessage = 'Network connection error. Please check your internet connection.';
        } else if (process.env.NODE_ENV === 'development') {
            userMessage = error.message;
        }

        setAuthError(userMessage);
        setUserStatus('error');
        errorHandled.current = true;
    }, [handleUnauthenticatedUser]);

    /**
     * Main initialization effect - runs once when component mounts
     * Also handles redirect callback from MSAL authentication
     */
    useEffect(() => {
        // Reset initialization flag to ensure it runs
        initializationAttempted.current = false;
        errorHandled.current = false;
        
        // Check if user just logged out - skip MSAL redirect handling
        const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
        if (isLoggingOut) {
            initializeApplication();
            return;
        }
        
        // Handle redirect promise for redirect login flow
        instance.handleRedirectPromise().then(async (response) => {
            if (response && response.account) {
                console.log('Redirect response received:', response.account.username);
                // Process the redirect login response
                try {
                    await AuthService.handleLoginSuccess(response);
                } catch (authError) {
                    console.error('Failed to process redirect login:', authError);
                }
            }
            initializeApplication();
        }).catch((error) => {
            console.error('Redirect handling failed:', error.errorCode, error.errorMessage);
            initializeApplication();
        });
        
    }, [initializeApplication, instance]);

    /**
     * Handle authentication state changes (for login only, not logout)
     * Detects when user logs in and reinitializes
     */
    useEffect(() => {
        // Only reinitialize if authenticated and no profile, but not during logout
        const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
        
        if (isAuthenticated && !userProfile && !errorHandled.current && !isLoggingOut) {
            // Small delay to ensure MSAL account is populated
            const accountTimer = setTimeout(() => {
                const accounts = instance.getAllAccounts();
                
                if (accounts.length > 0) {
                    initializationAttempted.current = false;
                    errorHandled.current = false;
                    initializeApplication();
                } else {
                    console.warn('No accounts found despite authentication');
                }
            }, 500);
            
            return () => clearTimeout(accountTimer);
        }
    }, [isAuthenticated, userProfile, instance, initializeApplication]);

    /**
     * Force initialization if stuck in loading state
     */
    useEffect(() => {
        const fallbackTimer = setTimeout(() => {
            if (isLoading && !initializationAttempted.current && !errorHandled.current) {
                initializationAttempted.current = false;
                initializeApplication();
            }
        }, 3000); // Reduced to 3 seconds

        return () => clearTimeout(fallbackTimer);
    }, [isLoading, initializeApplication]);

    /**
     * Render loading state during application initialization
     */
    const renderLoadingState = () => (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            bg={bgColor}
        >
            <VStack spacing={4}>
                <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="brand.500"
                    size="xl"
                />
                <Text color={textColor} fontSize="lg" fontWeight="medium">
                    Loading Prism Monitor...
                </Text>
                <Text color={textColor} fontSize="xs" opacity={0.5} mt={2}>
                    Initializing secure session...
                </Text>
                
                {/* User status indicator */}
                {userStatus && (
                    <Text fontSize="xs" color={
                        userStatus === 'pending' ? 'orange.500' :
                        userStatus === 'approved' ? 'green.500' :
                        userStatus === 'rejected' ? 'red.500' :
                        userStatus === 'suspended' ? 'red.600' :
                        'gray.500'
                    }>
                        Status: {userStatus.charAt(0).toUpperCase() + userStatus.slice(1)}
                    </Text>
                )}
                
            </VStack>
        </Box>
    );

    /**
     * Render error state for application failures
     * Includes user status-specific messaging
     */
    const renderErrorState = () => (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            bg={bgColor}
            p={8}
        >
            <VStack spacing={6} textAlign="center" maxW="lg">
                <Text fontSize="2xl" fontWeight="bold" color={
                    userStatus === 'pending' ? 'orange.500' :
                    userStatus === 'rejected' ? 'red.500' :
                    userStatus === 'suspended' ? 'red.600' :
                    'red.500'
                }>
                    {userStatus === 'pending' ? 'Account Pending Approval' :
                     userStatus === 'rejected' ? 'Account Rejected' :
                     userStatus === 'suspended' ? 'Account Suspended' :
                     'Application Error'}
                </Text>
                <Text color={textColor} fontSize="md" lineHeight="1.6">
                    {authError || 'An unexpected error occurred while loading the application.'}
                </Text>
                
                {/* Status-specific actions */}
                <VStack spacing={3}>
                    {(userStatus === 'pending' || userStatus === 'rejected' || userStatus === 'suspended') ? (
                        <Box>
                            <button
                                onClick={() => {
                                    AuthService.logout();
                                }}
                                style={{
                                    backgroundColor: '#4318ff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500'
                                }}
                            >
                                Sign Out
                            </button>
                        </Box>
                    ) : (
                        <Box>
                            <button
                                onClick={() => {
                                    setAuthError(null);
                                    setUserStatus(null);
                                    setAuthServiceReady(false);
                                    authServiceInitialized.current = false;
                                    initializationAttempted.current = false;
                                    errorHandled.current = false;
                                    window.location.reload();
                                }}
                                style={{
                                    backgroundColor: '#4318ff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    marginRight: '12px'
                                }}
                            >
                                Reload Application
                            </button>
                            <button
                                onClick={() => {
                                    handleUnauthenticatedUser();
                                    setAuthError(null);
                                    setUserStatus(null);
                                    setAuthServiceReady(false);
                                    authServiceInitialized.current = false;
                                    initializationAttempted.current = false;
                                    errorHandled.current = false;
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: '#4318ff',
                                    border: '2px solid #4318ff',
                                    padding: '10px 22px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500'
                                }}
                            >
                                Reset Session
                            </button>
                        </Box>
                    )}
                    

                </VStack>
            </VStack>
        </Box>
    );

    // Show loading state during initialization
    if (isLoading) {
        return renderLoadingState();
    }

    // Show error state if initialization failed
    if (authError) {
        return renderErrorState();
    }

    // Render main application with routing and enhanced notification systems
    return (
        <Box minH="100vh" bg={bgColor}>
            <Routes 
                userProfile={userProfile}
                appConfig={appConfig}
                onError={handleApplicationError}
                isAuthenticated={isAuthenticated}
                authServiceReady={authServiceReady}
                userStatus={userStatus}
            />
            
            {/* Enhanced Toast notification systems with fixed positioning */}
            <Toaster
                position="top-right"
                reverseOrder={false}
                gutter={8}
                containerClassName=""
                containerStyle={{
                    top: 20,
                    left: 20,
                    bottom: 20,
                    right: 20,
                }}
                toastOptions={{
                    className: '',
                    duration: appConfig?.app?.features?.notifications?.autoHideDuration || 5000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        padding: '16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        maxWidth: '400px'
                    },
                    success: {
                        duration: 4000,
                        style: {
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#fff'
                        },
                        iconTheme: {
                            primary: '#fff',
                            secondary: '#10b981'
                        }
                    },
                    error: {
                        duration: 6000,
                        style: {
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff'
                        },
                        iconTheme: {
                            primary: '#fff',
                            secondary: '#ef4444'
                        }
                    },
                    loading: {
                        style: {
                            background: 'linear-gradient(135deg, #4318ff 0%, #3311cc 100%)',
                            color: '#fff'
                        }
                    }
                }}
            />

            {/* Additional Toaster for bottom-right notifications (alert notifications) */}
            <Toaster
                position="bottom-right"
                reverseOrder={false}
                gutter={8}
                containerClassName="notification-toaster"
                containerStyle={{
                    bottom: 20,
                    right: 20,
                }}
                toastOptions={{
                    className: 'notification-toast',
                    duration: 5000,
                    style: {
                        background: '#fff',
                        color: '#2d3748',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        padding: '12px 16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        maxWidth: '350px',
                        minWidth: '300px'
                    },
                    success: {
                        style: {
                            background: '#f0fff4',
                            borderColor: '#10b981',
                            color: '#065f46'
                        },
                        iconTheme: {
                            primary: '#10b981',
                            secondary: '#f0fff4'
                        }
                    },
                    error: {
                        style: {
                            background: '#fef2f2',
                            borderColor: '#ef4444',
                            color: '#991b1b'
                        },
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fef2f2'
                        }
                    },
                    loading: {
                        style: {
                            background: '#eff6ff',
                            borderColor: '#3b82f6',
                            color: '#1e40af'
                        }
                    }
                }}
            />
        </Box>
    );
}

export default App;